import {
  CheckIcon,
  ExclamationCircleIcon,
  EyeIcon,
  EyeSlashIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import type { JSX } from 'react';
import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '../../lib/utils';

interface InputError {
  message: string;
  type?: 'error' | 'warning' | 'info';
}

interface CustomInputProps {
  label?: string;
  required?: boolean;
  options?: { value: string | number; label: string }[];
  error?: InputError | string;
  inputPrefix?: JSX.Element;
}

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'prefix'>, CustomInputProps {}

// Error-state border/ring, lifted verbatim from HEC-MIS's Input component
function getErrorStyling(error?: InputError): string {
  if (!error) return '';
  switch (error.type) {
    case 'warning':
      return 'border-yellow-300 focus-within:ring-yellow-500';
    case 'info':
      return 'border-blue-300 focus-within:ring-blue-500';
    default:
      return 'border-red-300 focus-within:ring-red-500';
  }
}

function getErrorTextColor(error?: InputError): string {
  if (!error) return '';
  switch (error.type) {
    case 'warning':
      return 'text-yellow-600';
    case 'info':
      return 'text-blue-600';
    default:
      return 'text-red-600';
  }
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className = '', type = 'text', label, required, options, error: errorProp, inputPrefix, id, ...props }, ref) => {
    const [showPassword, setShowPassword] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    const error: InputError | undefined = useMemo(() => {
      if (errorProp == null || errorProp === '') return undefined;
      if (typeof errorProp === 'string') return { message: errorProp, type: 'error' };
      return errorProp;
    }, [errorProp]);

    const isReadOnly = !!(props.readOnly || props.disabled);

    const filteredOptions = useMemo(() => {
      if (!options) return [];
      if (searchTerm.trim() === '') return options;
      const lower = searchTerm.trim().toLowerCase();
      return options.filter(
        o => o.label.toLowerCase().includes(lower) || String(o.value).toLowerCase().includes(lower)
      );
    }, [options, searchTerm]);

    useEffect(() => {
      if (isDropdownOpen && searchInputRef.current) searchInputRef.current.focus();
    }, [isDropdownOpen]);

    useEffect(() => {
      const handleClickOutside = (e: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
          setIsDropdownOpen(false);
          setSearchTerm('');
        }
      };
      if (isDropdownOpen) {
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
      }
    }, [isDropdownOpen]);

    const inputId = id || `input-${Math.random().toString(36).slice(2, 11)}`;

    const LabelNode = label && (
      <p className='text-black font-medium text-sm'>
        {label}
        {required && <sup className='text-red-500 text-md font-black'>*</sup>}
      </p>
    );

    // ─── Select ──────────────────────────────────────────────────────────────
    if (type === 'select') {
      const selectedOption = options?.find(option => option.value === props.value);
      const displayText = selectedOption ? selectedOption.label : props.placeholder || 'Select an option';

      return (
        <div className={cn('w-full flex flex-col gap-2', className)}>
          {LabelNode}

          <div className='relative' ref={dropdownRef}>
            <div
              className={cn(
                'w-full border-slate-100 rounded-lg bg-white border-2 gap-4 flex items-center relative h-[46px]',
                error ? getErrorStyling(error) : ''
              )}
            >
              {inputPrefix && inputPrefix}
              <button
                id={inputId}
                type='button'
                onClick={() => {
                  if (!isReadOnly) {
                    setIsDropdownOpen(!isDropdownOpen);
                    if (!isDropdownOpen) setSearchTerm('');
                  }
                }}
                className={cn(
                  'w-full py-3 focus:ring-2 rounded-lg ring-primary/10 ring-0 focus:outline-none px-5 duration-100 outline-none bg-transparent text-left h-full flex items-center justify-between',
                  isReadOnly ? 'cursor-not-allowed bg-slate-50' : 'cursor-pointer hover:bg-gray-50'
                )}
                disabled={isReadOnly}
              >
                <span className={cn('truncate min-w-0', !selectedOption && props.placeholder ? 'text-gray-400' : 'text-black')}>
                  {displayText}
                </span>
                <svg
                  width='12'
                  height='8'
                  viewBox='0 0 12 8'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                  className={cn('transition-transform duration-200 flex-shrink-0 ml-2', isDropdownOpen && 'rotate-180')}
                >
                  <path d='M1 1.5L6 6.5L11 1.5' stroke='#6B7280' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
                </svg>
              </button>
            </div>

            {isDropdownOpen && !isReadOnly && (
              <div className='absolute top-full left-0 right-0 mt-1 bg-white border-2 border-slate-100 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto'>
                <div className='px-4 py-2'>
                  <div className='relative'>
                    <MagnifyingGlassIcon className='absolute left-2.5 top-2.5 w-4 h-4 text-slate-400' />
                    <input
                      ref={searchInputRef}
                      type='text'
                      value={searchTerm}
                      className='w-full pl-8 pr-2 py-1 text-sm border border-slate-200 rounded focus:ring-2 focus:ring-blue-100 outline-none'
                      placeholder='Search...'
                      onChange={e => setSearchTerm(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') e.preventDefault();
                      }}
                    />
                  </div>
                </div>

                {props.placeholder && !selectedOption && (
                  <button
                    type='button'
                    onClick={() => {
                      props.onChange?.({ target: { value: '' } } as React.ChangeEvent<HTMLInputElement>);
                      setIsDropdownOpen(false);
                      setSearchTerm('');
                    }}
                    className='w-full px-5 py-3 text-left hover:bg-gray-50 text-gray-400 border-b border-gray-100 last:border-b-0'
                  >
                    {props.placeholder}
                  </button>
                )}

                {filteredOptions.length === 0 && (
                  <div className='px-5 py-3 text-gray-400 text-sm select-none'>No options found</div>
                )}

                {filteredOptions.map(option => (
                  <button
                    key={option.value}
                    type='button'
                    onClick={() => {
                      props.onChange?.({ target: { value: option.value } } as unknown as React.ChangeEvent<HTMLInputElement>);
                      setIsDropdownOpen(false);
                      setSearchTerm('');
                    }}
                    className={cn(
                      'w-full px-5 py-3 text-left hover:bg-gray-50 transition-colors duration-150 border-b border-gray-100 last:border-b-0',
                      option.value === props.value ? 'bg-blue-50 text-blue-600 font-medium' : 'text-black'
                    )}
                  >
                    {option.label}
                    {option.value === props.value && (
                      <span className='float-right'>
                        <CheckIcon className='w-4 h-4' />
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {error && (
            <p className={cn('font-medium flex items-center gap-1 mt-1 text-xs', getErrorTextColor(error))}>
              <ExclamationCircleIcon className='w-3.5 h-3.5' />
              {error.message}
            </p>
          )}
        </div>
      );
    }

    // ─── Textarea ────────────────────────────────────────────────────────────
    if (type === 'textarea') {
      return (
        <div className={cn('w-full flex flex-col gap-1 relative bg-transparent text-black', className)}>
          {LabelNode}
          <textarea
            readOnly={props.readOnly}
            disabled={props.disabled}
            className='w-full py-3 outline-none placeholder:text-slate-400 placeholder:text-sm border-2 border-slate-100 rounded-lg px-4 min-h-[140px] resize-none duration-100 bg-transparent'
            placeholder={props.placeholder}
            value={props.value}
            onChange={props.onChange as unknown as React.ChangeEventHandler<HTMLTextAreaElement>}
          />
          {error && (
            <p className={cn('font-medium flex items-center gap-1 mt-1 text-xs', getErrorTextColor(error))}>
              <ExclamationCircleIcon className='w-3.5 h-3.5' />
              {error.message}
            </p>
          )}
        </div>
      );
    }

    // ─── Standard input (text / number / date / password / search / email / …) ──
    return (
      <div className={cn('w-full flex flex-col gap-1 relative bg-transparent text-black', className)}>
        {LabelNode}
        <div
          className={cn(
            'w-full border-slate-100 rounded-lg bg-white border-2 gap-4 flex items-center h-[46px]',
            error ? getErrorStyling(error) : ''
          )}
        >
          {inputPrefix && inputPrefix}
          <input
            ref={ref}
            id={inputId}
            type={type === 'password' ? (showPassword ? 'text' : 'password') : type}
            className={cn(
              'w-full py-3 focus:ring-2 rounded-lg ring-primary/10 ring-0 focus:outline-none px-5 duration-100 outline-none h-full min-w-0',
              props.readOnly && 'cursor-not-allowed',
              props.readOnly ? 'bg-slate-50' : 'bg-transparent'
            )}
            {...props}
          />
          {type === 'password' && (
            <button
              onClick={() => setShowPassword(!showPassword)}
              className='right-[18px] absolute w-fit outline-none text-sm text-slate-400'
              type='button'
              tabIndex={-1}
            >
              {showPassword ? <EyeSlashIcon className='w-4 h-4' /> : <EyeIcon className='w-4 h-4' />}
            </button>
          )}
        </div>
        {error && (
          <p className={cn('font-medium flex items-center gap-1 mt-1 text-xs', getErrorTextColor(error))}>
            <ExclamationCircleIcon className='w-3.5 h-3.5' />
            {error.message}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export { Input };
